<script setup>
import PartnerActionsCell from "@frontJs/components/tables/cells/projectManagement/apikom/PartnerActionsCell.vue";
import PartnerDetailsCell from "@frontJs/components/tables/cells/projectManagement/apikom/PartnerDetailsCell.vue";
import { PlusCircleIcon, PlusIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { userSessionKey, archived } = defineProps({
  userSessionKey: {
    type: String,
    required: false,
  },
  archived: {
    type: Boolean,
    required: false,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  data,
  count,
  pagination,
  pageCount,
  sorting,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        props.row.original.url
          ? h(LinkCellV2, {
              text: props.row.original.name,
              href: props.row.original.url,
            })
          : h(TextCellV2, {
              text: props.row.original.name,
            }),
    }),
    columnHelper.display({
      id: "details",
      header: t("label.details"),
      cell: (innerProps) =>
        h(PartnerDetailsCell, {
          row: innerProps.row,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "contactName",
      header: () => t("label.name"),
      cell: (props) =>
        props.row.original.contacts.length > 0 &&
        props.row.original.contacts[0].name
          ? h(TextCellV2, {
              class: "uppercase",
              text: props.row.original.contacts[0].name,
              tooltipIcon: PlusCircleIcon,
              tooltipPlacement: "bottom",
              tooltipContent:
                props.row.original.contacts.length > 1 &&
                props.row.original.contacts
                  .slice(1)
                  .filter((contact) => contact.name).length > 0
                  ? h(
                      "ul",
                      {
                        class: "list-disc list-inside !m-0",
                      },
                      [
                        ...props.row.original.contacts
                          .slice(1)
                          .filter((contact) => contact.name)
                          .map((contact) =>
                            h("li", {
                              innerHTML: contact.name,
                            }),
                          ),
                      ],
                    )
                  : null,
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "contactEmail",
      header: () => t("label.email"),
      cell: (props) =>
        props.row.original.contacts.length > 0 &&
        props.row.original.contacts[0].email
          ? h(TextCellV2, {
              text: props.row.original.contacts[0].email,
              tooltipIcon: PlusCircleIcon,
              tooltipPlacement: "bottom",
              tooltipContent:
                props.row.original.contacts.length > 1 &&
                props.row.original.contacts
                  .slice(1)
                  .filter((contact) => contact.email).length > 0
                  ? h(
                      "ul",
                      {
                        class: "list-disc list-inside !m-0 px-2 py-1",
                      },
                      [
                        ...props.row.original.contacts
                          .slice(1)
                          .filter((contact) => contact.email)
                          .map((contact) =>
                            h("li", {
                              innerHTML:
                                "<b class='uppercase'>" +
                                contact.name +
                                " :</b> " +
                                contact.email,
                            }),
                          ),
                      ],
                    )
                  : null,
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "contactPhone",
      header: () => t("label.telephone"),
      cell: (props) =>
        props.row.original.contacts.length > 0 &&
        props.row.original.contacts[0].phone
          ? h(TextCellV2, {
              text: props.row.original.contacts[0].phone,
              tooltipIcon: PlusCircleIcon,
              tooltipPlacement: "bottom",
              tooltipContent:
                props.row.original.contacts.length > 1 &&
                props.row.original.contacts
                  .slice(1)
                  .filter((contact) => contact.phone).length > 0
                  ? h(
                      "ul",
                      {
                        class: "list-disc list-inside !m-0 px-2 py-1",
                      },
                      [
                        ...props.row.original.contacts
                          .slice(1)
                          .filter((contact) => contact.phone)
                          .map((contact) =>
                            h("li", {
                              innerHTML:
                                "<b class='uppercase'>" +
                                contact.name +
                                " :</b> " +
                                contact.phone,
                            }),
                          ),
                      ],
                    )
                  : null,
            })
          : h(CrossIconCell),
      enableSorting: false,
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(PartnerActionsCell, {
          row: innerProps.row,
        }),
    }),
  ],
  url: route("front.projectManagement.apikom.postPartners"),
  data: {
    archived: archived,
  },
  userSessionKey: userSessionKey,
  columnPinning: {
    right: ['actions']
  }
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :filters="filters"
      :available-filters="availableFilters"
      :set-filters="setFilters"
      :set-global-filter="setGlobalFilter"
    >
      <template #actions>
        <ActionButton
          size="small"
          :href="route('front.projectManagement.apikom.createPartner')"
          :left-icon="PlusIcon"
        >
          {{ t("button.add") }}
        </ActionButton>
      </template>
    </DataTableLayout>
  </div>
</template>
